import React, { useState, useEffect } from 'react';
import { ArrowLeft, Package, Clock, CheckCircle, AlertCircle, ShoppingBag, ChevronDown, ChevronUp, Truck, MapPin, Navigation, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { auth } from '../firebase';
import { formatPrice } from '../utils';

interface OrderItem {
  id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  price: number;
}

interface Order {
  id: number;
  order_number: number;
  total_amount: number;
  status: string;
  shipping_name: string;
  shipping_email: string;
  shipping_phone: string;
  shipping_address: string;
  shipping_pincode: string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  created_at: string;
  paid_at: string;
  order_items: OrderItem[];
  // Shiprocket shipping fields
  shiprocket_order_id: string | null;
  shipment_id: string | null;
  awb_number: string | null;
  courier_name: string | null;
  shipping_status: string | null;
}

interface TrackingData {
  status: string;
  awbNumber: string | null;
  courierName: string | null;
  tracking: any;
}

interface OrderHistoryProps {
  isOpen: boolean;
  onClose: () => void;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const statusConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  PAID: { icon: <CheckCircle className="w-4 h-4" />, color: 'text-green-400 bg-green-400/10 border-green-400/30', label: 'Paid' },
  PENDING: { icon: <Clock className="w-4 h-4" />, color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30', label: 'Pending' },
  FAILED: { icon: <AlertCircle className="w-4 h-4" />, color: 'text-red-400 bg-red-400/10 border-red-400/30', label: 'Failed' },
};

const shippingStatusConfig: Record<string, { icon: React.ReactNode; color: string; label: string; step: number }> = {
  PROCESSING: { icon: <Package className="w-4 h-4" />, color: 'text-blue-400 bg-blue-400/10 border-blue-400/30', label: 'Processing', step: 1 },
  SHIPPED: { icon: <Truck className="w-4 h-4" />, color: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30', label: 'Shipped', step: 2 },
  IN_TRANSIT: { icon: <Navigation className="w-4 h-4" />, color: 'text-orange-400 bg-orange-400/10 border-orange-400/30', label: 'In Transit', step: 3 },
  DELIVERED: { icon: <CheckCircle className="w-4 h-4" />, color: 'text-green-400 bg-green-400/10 border-green-400/30', label: 'Delivered', step: 4 },
  CANCELLED: { icon: <AlertCircle className="w-4 h-4" />, color: 'text-red-400 bg-red-400/10 border-red-400/30', label: 'Cancelled', step: 0 },
};

const TRACKING_STEPS = ['Processing', 'Shipped', 'In Transit', 'Delivered'];

const OrderHistory: React.FC<OrderHistoryProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [trackingLoading, setTrackingLoading] = useState<number | null>(null);
  const [trackingData, setTrackingData] = useState<Record<number, TrackingData>>({});

  useEffect(() => {
    if (isOpen && user) {
      fetchOrders();
    }
  }, [isOpen, user]);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/orders`, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });

      const data = await response.json();
      if (response.ok) {
        setOrders(data.orders || []);
      } else {
        setError(data.error || 'Failed to fetch orders');
      }
    } catch {
      setError('Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const trackOrder = async (orderNumber: number) => {
    setTrackingLoading(orderNumber);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/track/${orderNumber}`, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });

      const data = await response.json();
      if (response.ok) {
        setTrackingData(prev => ({ ...prev, [orderNumber]: data }));
      }
    } catch {
      // Silently fail — tracking is optional
    } finally {
      setTrackingLoading(null);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getShippingStep = (status: string | null) => {
    if (!status) return 0;
    return shippingStatusConfig[status]?.step || 0;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black overflow-y-auto animate-fade-in no-scrollbar">
      <div className="max-w-4xl mx-auto py-12 px-6">
        {/* Header */}
        <header className="flex justify-between items-center mb-12">
          <button onClick={onClose} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-neutral-500 hover:text-white transition-colors">
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <div className="text-xl font-black italic">YUME<span className="text-amber-500">CAV</span>_ORDERS</div>
        </header>

        {/* User Info */}
        {user && (
          <div className="mb-10 p-6 bg-neutral-900/50 rounded-2xl border border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-600/20 rounded-xl border border-amber-500/30 flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-neutral-500">Logged in as</p>
                <p className="font-bold text-sm">{user.email || user.phoneNumber || 'User'}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-xs font-black uppercase tracking-widest text-neutral-500">Total Orders</p>
                <p className="text-2xl font-black text-amber-500">{orders.length}</p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 opacity-40">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-xs font-black uppercase tracking-widest">Loading orders...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-red-400 text-xs font-bold uppercase tracking-wide text-center">
            {error}
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 opacity-40">
            <Package className="w-12 h-12 mb-4 stroke-1" />
            <p className="font-black uppercase tracking-[0.3em] text-xs mb-2">No Orders Yet</p>
            <p className="text-neutral-500 text-xs">Your order history will appear here</p>
            <button onClick={onClose} className="mt-6 text-[10px] text-amber-500 border-b border-amber-500 uppercase font-black tracking-widest pb-1">
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-xs font-black uppercase tracking-widest text-neutral-500 mb-6 flex items-center gap-2">
              <Package className="w-4 h-4" /> Order History
            </h2>

            {orders.map((order) => {
              const status = statusConfig[order.status] || statusConfig.PENDING;
              const isExpanded = expandedOrder === order.id;
              const shippingStatus = order.shipping_status ? shippingStatusConfig[order.shipping_status] : null;
              const orderTracking = trackingData[order.order_number];

              return (
                <div key={order.id} className="bg-neutral-900/50 rounded-2xl border border-white/5 overflow-hidden hover:border-white/10 transition-colors">
                  {/* Order Header - Clickable */}
                  <button
                    onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                    className="w-full p-6 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className={`px-3 py-1.5 rounded-lg border flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${status.color}`}>
                        {status.icon}
                        {status.label}
                      </div>
                      {/* Shipping Status Badge */}
                      {shippingStatus && (
                        <div className={`px-3 py-1.5 rounded-lg border flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${shippingStatus.color}`}>
                          {shippingStatus.icon}
                          {shippingStatus.label}
                        </div>
                      )}
                      <div>
                        <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest">
                          {formatDate(order.created_at)}
                        </p>
                        <p className="text-xs font-black mt-0.5">
                          YMC-{String(order.order_number || 0).padStart(5, '0')} · {order.order_items?.length || 0} item{(order.order_items?.length || 0) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-black tracking-tighter text-amber-500">
                        {formatPrice(order.total_amount)}
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-6 pb-6 border-t border-white/5 pt-4 space-y-4 animate-fade-in">
                      {/* Order Items */}
                      <div className="space-y-2">
                        {order.order_items?.map((item) => (
                          <div key={item.id} className="flex justify-between items-center text-[10px] font-black uppercase py-2">
                            <span className="text-neutral-400">{item.product_name} × {item.quantity}</span>
                            <span>{formatPrice(item.price * item.quantity)}</span>
                          </div>
                        ))}
                      </div>

                      {/* Shipping & Payment Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/5">
                        <div>
                          <p className="text-[9px] text-neutral-500 font-black uppercase tracking-widest mb-2">Shipping</p>
                          <p className="text-xs font-bold">{order.shipping_name}</p>
                          <p className="text-[10px] text-neutral-400">{order.shipping_address}</p>
                          <p className="text-[10px] text-neutral-400">PIN: {order.shipping_pincode}</p>
                          <p className="text-[10px] text-neutral-400">{order.shipping_phone}</p>
                        </div>
                        <div>
                          <p className="text-[9px] text-neutral-500 font-black uppercase tracking-widest mb-2">Order ID</p>
                          <p className="text-xs font-bold text-amber-400">YMC-{String(order.order_number || 0).padStart(5, '0')}</p>
                          {order.paid_at && (
                            <p className="text-[10px] text-green-400 mt-1">Paid: {formatDate(order.paid_at)}</p>
                          )}
                        </div>
                      </div>

                      {/* === SHIPPING TRACKING SECTION === */}
                      {(order.status === 'PAID' || order.shipping_status) && (
                        <div className="pt-4 border-t border-white/5">
                          <div className="flex items-center justify-between mb-4">
                            <p className="text-[9px] text-neutral-500 font-black uppercase tracking-widest flex items-center gap-2">
                              <Truck className="w-3.5 h-3.5" /> Shipment Tracking
                            </p>
                            {order.order_number && (
                              <button
                                onClick={(e) => { e.stopPropagation(); trackOrder(order.order_number); }}
                                disabled={trackingLoading === order.order_number}
                                className="text-[9px] font-black uppercase tracking-widest text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                              >
                                {trackingLoading === order.order_number ? (
                                  <><Loader2 className="w-3 h-3 animate-spin" /> Tracking...</>
                                ) : (
                                  <><ExternalLink className="w-3 h-3" /> Track Order</>
                                )}
                              </button>
                            )}
                          </div>

                          {/* Shipping Info Cards */}
                          {(order.courier_name || order.awb_number) && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                              {order.courier_name && (
                                <div className="bg-neutral-800/50 rounded-xl p-3 border border-white/5">
                                  <p className="text-[8px] text-neutral-500 font-black uppercase tracking-widest mb-1">Courier</p>
                                  <p className="text-[11px] font-bold text-white">{order.courier_name}</p>
                                </div>
                              )}
                              {order.awb_number && (
                                <div className="bg-neutral-800/50 rounded-xl p-3 border border-white/5">
                                  <p className="text-[8px] text-neutral-500 font-black uppercase tracking-widest mb-1">AWB Number</p>
                                  <p className="text-[11px] font-mono font-bold text-white">{order.awb_number}</p>
                                </div>
                              )}
                              {order.shipping_status && (
                                <div className="bg-neutral-800/50 rounded-xl p-3 border border-white/5">
                                  <p className="text-[8px] text-neutral-500 font-black uppercase tracking-widest mb-1">Status</p>
                                  <p className={`text-[11px] font-bold ${shippingStatus?.color.split(' ')[0] || 'text-white'}`}>
                                    {shippingStatus?.label || order.shipping_status}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Shipping Progress Bar */}
                          {order.shipping_status && order.shipping_status !== 'CANCELLED' && (
                            <div className="mb-4">
                              <div className="relative flex items-start justify-between">
                                {/* Background line — sits BEHIND the circles */}
                                <div className="absolute top-3 left-[12.5%] right-[12.5%] h-0.5 bg-neutral-700 rounded-full z-0" />
                                {/* Active progress line */}
                                <div 
                                  className="absolute top-3 left-[12.5%] h-0.5 bg-amber-500 rounded-full z-0 transition-all duration-700"
                                  style={{ width: `${Math.max(0, ((getShippingStep(order.shipping_status) - 1) / (TRACKING_STEPS.length - 1)) * 75)}%` }}
                                />
                                {/* Step circles — on top of the line */}
                                {TRACKING_STEPS.map((step, idx) => {
                                  const currentStep = getShippingStep(order.shipping_status);
                                  const isActive = idx + 1 <= currentStep;
                                  const isCurrent = idx + 1 === currentStep;
                                  return (
                                    <div key={step} className="flex flex-col items-center flex-1 relative z-10">
                                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-black border-2 transition-all duration-500 ${
                                        isActive 
                                          ? 'bg-amber-500 border-amber-500 text-white' 
                                          : 'bg-neutral-800 border-neutral-600 text-neutral-500'
                                      } ${isCurrent ? 'ring-2 ring-amber-500/30 ring-offset-2 ring-offset-neutral-900' : ''}`}>
                                        {isActive ? '✓' : idx + 1}
                                      </div>
                                      <p className={`text-[8px] font-black uppercase tracking-wider mt-1.5 ${
                                        isActive ? 'text-amber-400' : 'text-neutral-600'
                                      }`}>{step}</p>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* No Shipping Yet Message */}
                          {!order.shipping_status && order.status === 'PAID' && (
                            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 flex items-center gap-3">
                              <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0" />
                              <p className="text-[10px] text-blue-300 font-bold">
                                Your order has been confirmed and is being prepared for shipment. Tracking info will appear here once dispatched.
                              </p>
                            </div>
                          )}

                          {/* Live Tracking Data from Shiprocket */}
                          {orderTracking && (
                            <div className="bg-neutral-800/30 rounded-xl p-4 border border-white/5 mt-3">
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-[9px] text-neutral-500 font-black uppercase tracking-widest">Live Tracking Updates</p>
                                {orderTracking.tracking?.etd && (
                                  <p className="text-[9px] text-amber-400 font-bold">ETA: {orderTracking.tracking.etd}</p>
                                )}
                              </div>
                              {orderTracking.tracking?.activities && orderTracking.tracking.activities.length > 0 ? (
                                <div className="space-y-2 max-h-48 overflow-y-auto no-scrollbar">
                                  {orderTracking.tracking.activities.slice(0, 10).map((activity: any, idx: number) => (
                                    <div key={idx} className="flex items-start gap-3 text-[10px]">
                                      <div className={`w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${idx === 0 ? 'bg-green-400' : 'bg-amber-500'}`} />
                                      <div>
                                        <p className="font-bold text-neutral-300">{activity.status}</p>
                                        <p className="text-neutral-500">{activity.location} · {activity.date}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-[10px] text-neutral-500">
                                  {orderTracking.courierName 
                                    ? `Courier: ${orderTracking.courierName}. Tracking details will be available once the shipment is picked up.`
                                    : order.shiprocket_order_id
                                      ? 'Shipment is being processed. Detailed tracking will be available once a courier is assigned.'
                                      : 'Order is being prepared. Shiprocket tracking will appear here once the shipment is created.'}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;
